import { useCallback, useEffect, useRef, useState } from 'react';
import { insuranceApi } from './api';

/**
 * Trae el step de seguros + insurance asociado (si existe).
 */
export function useInsuranceStep(qpId) {
  const [data, setData] = useState({ step: null, insurance: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    console.log("[Seguros Hook] useInsuranceStep refresh, qpId=", qpId);
    if (!qpId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await insuranceApi.show(qpId);
      console.log("[Seguros Hook] show response", res?.data);
      if (res?.data?.success) {
        setData({ step: res.data.step, insurance: res.data.insurance });
      }
    } catch (e) {
      console.error("[Seguros Hook] show FAILED", {
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [qpId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...data, loading, error, refresh };
}

/**
 * Polling de la extracción AI sobre un document_id concreto.
 * Se detiene automáticamente al llegar a 'done' o 'failed'.
 *
 * Devuelve (formato nuevo del prompt unificado SEGURO):
 *   - documentType: 'policy' | 'payments' | 'policy_and_payments' | 'unrelated' | null
 *   - policy: objeto con campos de la póliza (puede ser null)
 *   - payments: array de pagarés detectados (puede estar vacío)
 *   - warnings: array de strings (validation.warnings)
 *   - accuracy: número 0–100 (si lo provee la IA)
 *   - finding: texto opcional con la observación de la IA
 *   - data: objeto crudo `extracted` por compatibilidad
 *   - paymentsDetected: alias retrocompatible (=== payments)
 *   - category: 'POLIZA' | 'PAGARE' | null  (inferido a partir de document_type)
 */
export function useExtractionPolling(documentId, enabled, intervalMs = 2000) {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentsDetected, setPaymentsDetected] = useState([]);
  const [accuracy, setAccuracy] = useState(null);
  const [finding, setFinding] = useState(null);
  const [category, setCategory] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled || !documentId) return undefined;
    cancelledRef.current = false;
    // reset al cambiar de documento
    setStatus('pending');
    setData(null);
    setError(null);
    setPaymentsDetected([]);
    setAccuracy(null);
    setFinding(null);
    setCategory(null);
    setDocumentType(null);
    setPolicy(null);
    setWarnings([]);

    const poll = async () => {
      try {
        const res = await insuranceApi.getExtraction(documentId);
        if (cancelledRef.current) return;
        const next = res?.data?.status ?? 'none';
        setStatus(next);
        if (next === 'done') {
          // payload puede venir como res.data.data = { extracted, accuracy, status, finding }
          const raw = res?.data?.data ?? null;
          let extracted = raw;
          let acc = null;
          let find = null;
          if (raw && typeof raw === 'object' && raw.extracted) {
            extracted = raw.extracted;
            acc = raw.accuracy ?? null;
            find = raw.finding ?? null;
          }
          // accuracy puede venir 0–1 o 0–100. Normalizamos a 0–100.
          if (acc != null) {
            const n = Number(acc);
            acc = n <= 1 ? Math.round(n * 100) : Math.round(n);
          }
          setData(extracted ?? null);
          setAccuracy(acc);
          setFinding(find);

          // Nuevo formato unificado SEGURO
          const dt = extracted?.document_type ?? null;
          setDocumentType(dt);
          setPolicy(extracted?.policy ?? null);
          const pays = Array.isArray(extracted?.payments) ? extracted.payments : [];
          setPaymentsDetected(pays);
          const ws = extracted?.validation?.warnings;
          setWarnings(Array.isArray(ws) ? ws : []);

          // Categoría retrocompatible (legacy) — inferida desde document_type
          let cat = null;
          if (dt === 'policy' || dt === 'policy_and_payments') cat = 'POLIZA';
          else if (dt === 'payments') cat = 'PAGARE';
          setCategory(cat);
        } else if (next === 'failed') {
          setError(res.data.error || 'extracción fallida');
        } else if (next === 'pending') {
          setTimeout(poll, intervalMs);
        }
      } catch (e) {
        if (!cancelledRef.current) setError(e);
      }
    };

    poll();
    return () => { cancelledRef.current = true; };
  }, [documentId, enabled, intervalMs]);

  return {
    status, data, error,
    paymentsDetected, accuracy, finding, category,
    documentType, policy, warnings,
  };
}

/**
 * Polling múltiple: monta un useExtractionPolling por cada docId y devuelve
 * el estado agregado. Útil para esperar a que TODOS los PDFs subidos hayan
 * terminado su extracción antes de pedir el snapshot consolidado.
 *
 * NOTA: no se puede llamar useExtractionPolling en un loop con array dinámico
 * por las reglas de hooks. Por eso este hook hace su propio polling interno
 * usando un effect que abre N peticiones en paralelo y se detiene cuando
 * cada doc llega a done/failed.
 *
 * Devuelve:
 *   { ready: boolean, items: [{ docId, status, extracted, accuracy }], anyFailed: bool }
 */
export function useExtractionPollingMany(docIds, enabled = true, intervalMs = 2000) {
  const [items, setItems] = useState([]);
  const cancelledRef = useRef(false);
  const idsKey = (docIds || []).join(',');

  useEffect(() => {
    if (!enabled || !docIds || docIds.length === 0) {
      setItems([]);
      return undefined;
    }
    cancelledRef.current = false;
    setItems(docIds.map((docId) => ({
      docId,
      status: 'pending',
      extracted: null,
      accuracy: null,
    })));

    const pollOne = async (docId) => {
      try {
        const res = await insuranceApi.getExtraction(docId);
        if (cancelledRef.current) return;
        const next = res?.data?.status ?? 'none';
        if (next === 'done' || next === 'failed') {
          const raw = res?.data?.data ?? null;
          const extracted = (raw && typeof raw === 'object' && raw.extracted) ? raw.extracted : raw;
          let acc = (raw && typeof raw === 'object') ? (raw.accuracy ?? null) : null;
          if (acc != null) {
            const n = Number(acc);
            acc = n <= 1 ? Math.round(n * 100) : Math.round(n);
          }
          setItems((prev) => prev.map((it) =>
            it.docId === docId ? { ...it, status: next, extracted, accuracy: acc } : it
          ));
        } else {
          setTimeout(() => pollOne(docId), intervalMs);
        }
      } catch (e) {
        if (!cancelledRef.current) {
          setItems((prev) => prev.map((it) =>
            it.docId === docId ? { ...it, status: 'failed', extracted: null, accuracy: null } : it
          ));
        }
      }
    };

    docIds.forEach((id) => pollOne(id));
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, enabled, intervalMs]);

  const ready = items.length > 0 && items.every((it) => it.status === 'done' || it.status === 'failed');
  const anyFailed = items.some((it) => it.status === 'failed');

  return { ready, items, anyFailed };
}

/**
 * Trae el snapshot consolidado del backend (post-merge de TODOS los docs
 * subidos al step). Requiere que useExtractionPollingMany ya esté ready.
 */
export function useInsuranceConsolidated(qpId, ready) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!qpId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await insuranceApi.consolidated(qpId);
      if (res?.data?.success) {
        setData(res.data.consolidated || null);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [qpId]);

  useEffect(() => {
    if (ready) refresh();
  }, [ready, refresh]);

  return { data, loading, error, refresh };
}

/**
 * Trae los pagarés de un insurance dado.
 * Devuelve { payments, loading, error, refresh }.
 */
export function useInsurancePayments(insuranceId) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!insuranceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await insuranceApi.getInsurancePayments(insuranceId);
      const list =
        res?.data?.payments ??
        res?.data?.data ??
        (Array.isArray(res?.data) ? res.data : []);
      setPayments(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [insuranceId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { payments, loading, error, refresh };
}

/**
 * Carga el timeline de eventos del insurance.
 */
export function useInsuranceTimeline(insuranceId) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!insuranceId) return;
    setLoading(true);
    try {
      const res = await insuranceApi.timeline(insuranceId);
      if (res?.data?.success) setEvents(res.data.events ?? []);
    } finally {
      setLoading(false);
    }
  }, [insuranceId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { events, loading, refresh };
}

/**
 * Catálogo de proveedores activos.
 */
export function useProviders(type = 'insurance') {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    insuranceApi
      .providers(type)
      .then((res) => { if (active && res?.data?.success) setProviders(res.data.providers ?? []); })
      .catch(() => { if (active) setProviders([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [type]);

  return { providers, loading };
}
